/**
 * Интерфейс исполнителя: оперативная главная, заказы и кабинет витрины.
 *
 * Роль в базе называется `courier`, но означает «исполнитель»: он продаёт,
 * подтверждает и доставляет свои заказы. Основные разделы живут в нижней
 * навигации, а глубокие формы остаются отдельными экранами с системной
 * кнопкой «назад», чтобы fixed-кнопки форм не спорили с таббаром.
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  CurrentUser,
  Order,
  Vendor,
  VendorDayOff,
  VendorProduct,
  VendorScheduleDay,
  VendorZone,
} from '@arbuz/shared';
import { ApiError, api } from '../api';
import { makeT } from '../i18n';
import { haptic, showBackButton } from '../tg';
import { CourierHome } from './CourierHome';
import { CourierOrders } from './CourierOrders';
import { CourierOrderCard } from './CourierOrderCard';
import { CourierDeliver } from './CourierDeliver';
import { CourierFail } from './CourierFail';
import { CourierProfile } from './CourierProfile';
import { CourierProfileHub } from './CourierProfileHub';
import { ThemedIcon, type ThemedIconName } from './clientUi';
import { VendorProductEdit } from './VendorProductEdit';
import { VendorProducts } from './VendorProducts';
import { VendorPayout } from './VendorPayout';
import { VendorSchedule } from './VendorSchedule';
import { VendorShop } from './VendorShop';
import { VendorZoneEdit } from './VendorZoneEdit';
import { VendorZones } from './VendorZones';

type CourierScreen =
  | 'home'
  | 'orders'
  | 'order'
  | 'deliver'
  | 'fail'
  | 'profile'
  | 'profilePickup'
  | 'shop'
  | 'shopProducts'
  | 'shopProductEdit'
  | 'shopZones'
  | 'shopZoneEdit'
  | 'shopSchedule'
  | 'shopPayout';

type OrderScope = 'active' | 'completed';
type CourierTab = 'home' | 'orders' | 'shop' | 'profile';

const BACK_TO: Record<CourierScreen, CourierScreen | null> = {
  home: null,
  orders: 'home',
  order: 'orders',
  deliver: 'order',
  fail: 'order',
  profile: 'home',
  profilePickup: 'profile',
  shop: 'home',
  shopProducts: 'shop',
  shopProductEdit: 'shopProducts',
  shopZones: 'shop',
  shopZoneEdit: 'shopZones',
  shopSchedule: 'profile',
  shopPayout: 'profile',
};

const TAB_ICON: Record<CourierTab, ThemedIconName> = {
  home: 'home',
  orders: 'orders',
  shop: 'order',
  profile: 'profile',
};

export function CourierApp({ user }: { user: CurrentUser }) {
  const t = makeT(user.locale);

  const [screen, setScreen] = useState<CourierScreen>('home');
  const [scope, setScope] = useState<OrderScope>('active');
  const [ordersByScope, setOrdersByScope] = useState<Record<OrderScope, Order[] | null>>({
    active: null,
    completed: null,
  });
  const [orderErrors, setOrderErrors] = useState<Record<OrderScope, string | null>>({
    active: null,
    completed: null,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [scheduleBack, setScheduleBack] = useState<CourierScreen>('profile');
  const [payoutBack, setPayoutBack] = useState<CourierScreen>('profile');

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [products, setProducts] = useState<VendorProduct[] | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [zones, setZones] = useState<VendorZone[] | null>(null);
  const [zonesError, setZonesError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<VendorScheduleDay[] | null>(null);
  const [daysOff, setDaysOff] = useState<VendorDayOff[] | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<number | 'new' | null>(null);
  const [editingZone, setEditingZone] = useState<number | 'new' | null>(null);

  const loadOrders = useCallback(async (nextScope: OrderScope) => {
    try {
      const loaded = await api.get<Order[]>(`/api/courier/orders?scope=${nextScope}`);
      setOrdersByScope((current) => ({ ...current, [nextScope]: loaded }));
      setOrderErrors((current) => ({ ...current, [nextScope]: null }));
    } catch (err) {
      setOrderErrors((current) => ({
        ...current,
        [nextScope]: err instanceof ApiError ? err.message : t('networkError'),
      }));
    }
  }, [t]);

  /**
   * Один эффект на всё: и первая загрузка, и переключение вкладки. Отдельного
   * эффекта «загрузить active на старте» здесь быть не должно — на монтировании
   * срабатывали оба и слали один и тот же запрос дважды. После ошибки
   * повторяет только кнопка: иначе сломанный ответ превращается в цикл.
   */
  useEffect(() => {
    if (ordersByScope[scope] === null && orderErrors[scope] === null) {
      void loadOrders(scope);
    }
  }, [loadOrders, orderErrors, ordersByScope, scope]);

  const loadVendor = useCallback(async () => {
    try {
      setVendor(await api.get<Vendor>('/api/vendor'));
      setVendorError(null);
    } catch (err) {
      setVendor(null);
      // 404 — витрины ещё нет: это незаконченная настройка, а не ошибка,
      // и экран должен показать «витрины нет», а не красный баннер.
      if (err instanceof ApiError) setVendorError(err.status === 404 ? null : err.message);
      else setVendorError(t('networkError'));
    }
  }, [t]);

  useEffect(() => {
    void loadVendor();
  }, [loadVendor]);

  const loadProducts = useCallback(async () => {
    try {
      setProducts(await api.get<VendorProduct[]>('/api/vendor/products'));
      setProductsError(null);
    } catch (err) {
      setProductsError(err instanceof ApiError ? err.message : t('networkError'));
    }
  }, [t]);

  const loadZones = useCallback(async () => {
    try {
      setZones(await api.get<VendorZone[]>('/api/vendor/zones'));
      setZonesError(null);
    } catch (err) {
      setZonesError(err instanceof ApiError ? err.message : t('networkError'));
    }
  }, [t]);

  const loadSchedule = useCallback(async () => {
    try {
      const [res, off] = await Promise.all([
        api.get<{ days: VendorScheduleDay[] }>('/api/vendor/schedule'),
        api.get<VendorDayOff[]>('/api/vendor/days-off'),
      ]);
      setSchedule(res.days);
      setDaysOff(off);
      setScheduleError(null);
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : t('networkError'));
    }
  }, [t]);

  useEffect(() => {
    const back =
      screen === 'shopSchedule' ? scheduleBack :
      screen === 'shopPayout' ? payoutBack :
      BACK_TO[screen];
    if (!back) return;
    return showBackButton(() => setScreen(back));
  }, [payoutBack, scheduleBack, screen]);

  const selected =
    [...(ordersByScope.active ?? []), ...(ordersByScope.completed ?? [])].find(
      (o) => o.id === selectedId,
    ) ?? null;

  const openOrder = (id: number) => {
    setSelectedId(id);
    setScreen('order');
  };

  const afterAction = useCallback(async () => {
    await Promise.all([loadOrders('active'), loadOrders('completed')]);
    setScope('active');
    setScreen('orders');
    setSelectedId(null);
  }, [loadOrders]);

  const openProducts = () => {
    void loadProducts();
    setScreen('shopProducts');
  };

  const openZones = () => {
    void loadZones();
    setScreen('shopZones');
  };

  const openOrders = () => (
    <>
      <CourierOrders
        user={user}
        orders={ordersByScope[scope]}
        scope={scope}
        error={orderErrors[scope]}
        onScope={setScope}
        onOpen={openOrder}
        onRetry={() => void loadOrders(scope)}
      />
      {nav('orders')}
    </>
  );

  const nav = (active: CourierTab) => (
    <CourierBottomNav
      active={active}
      labels={{
        home: t('courierHome'),
        orders: t('orders'),
        shop: t('shopTitle'),
        profile: t('profile'),
      }}
      onTab={(tab) => {
        haptic('light');
        if (tab === 'orders') setScope('active');
        setScreen(tab);
      }}
    />
  );

  if ((screen === 'order' || screen === 'deliver' || screen === 'fail') && !selected) {
    if (ordersByScope.active !== null || ordersByScope.completed !== null) return openOrders();
  }

  switch (screen) {
    case 'home':
      return (
        <>
          <CourierHome
            user={user}
            activeOrders={ordersByScope.active}
            ordersError={orderErrors.active}
            vendor={vendor}
            vendorError={vendorError}
            onOpenOrder={openOrder}
            onOrders={() => {
              setScope('active');
              setScreen('orders');
            }}
            onProducts={openProducts}
            onZones={openZones}
            onProfile={() => setScreen('profile')}
            onRetryOrders={() => void loadOrders('active')}
            onRetryVendor={() => void loadVendor()}
          />
          {nav('home')}
        </>
      );

    case 'orders':
      return openOrders();

    case 'order':
      return selected ? (
        <CourierOrderCard
          user={user}
          order={selected}
          canConfirm={vendor?.can_confirm_orders ?? false}
          onChanged={() => {
            void loadOrders(scope);
          }}
          onDeliver={() => setScreen('deliver')}
          onFail={() => setScreen('fail')}
          onBack={() => setScreen('orders')}
        />
      ) : null;

    case 'deliver':
      return selected ? (
        <CourierDeliver user={user} order={selected} onDone={() => void afterAction()} />
      ) : null;

    case 'fail':
      return selected ? (
        <CourierFail user={user} order={selected} onDone={() => void afterAction()} />
      ) : null;

    case 'profile':
      return (
        <>
          <CourierProfileHub
            user={user}
            vendor={vendor}
            vendorError={vendorError}
            onPayout={() => {
              setPayoutBack('profile');
              setScreen('shopPayout');
            }}
            onSchedule={() => {
              setScheduleBack('profile');
              void loadSchedule();
              setScreen('shopSchedule');
            }}
            onPickup={() => setScreen('profilePickup')}
            onRetryVendor={() => void loadVendor()}
          />
          {nav('profile')}
        </>
      );

    case 'profilePickup':
      return <CourierProfile user={user} onBack={() => setScreen('profile')} />;

    case 'shop':
      return (
        <div className="courier-has-nav">
          <VendorShop
            user={user}
            vendor={vendor}
            loadError={vendorError}
            onReload={() => void loadVendor()}
            onVendor={setVendor}
            onProducts={openProducts}
            onZones={openZones}
            onSchedule={() => {
              setScheduleBack('shop');
              void loadSchedule();
              setScreen('shopSchedule');
            }}
            onPayout={() => {
              setPayoutBack('shop');
              setScreen('shopPayout');
            }}
            onBack={() => setScreen('home')}
          />
          {nav('shop')}
        </div>
      );

    case 'shopProducts':
      return (
        <VendorProducts
          user={user}
          products={products}
          error={productsError}
          onOpen={(id) => {
            setEditingProduct(id);
            setScreen('shopProductEdit');
          }}
          onRetry={() => void loadProducts()}
          onBack={() => setScreen('shop')}
        />
      );

    case 'shopProductEdit':
      return (
        <VendorProductEdit
          user={user}
          product={
            editingProduct === 'new' || editingProduct === null
              ? null
              : (products?.find((p) => p.id === editingProduct) ?? null)
          }
          onSaved={() => {
            void loadProducts();
            void loadVendor();
            setScreen('shopProducts');
          }}
          onBack={() => setScreen('shopProducts')}
        />
      );

    case 'shopZones':
      return (
        <VendorZones
          user={user}
          zones={zones}
          error={zonesError}
          onOpen={(id) => {
            setEditingZone(id);
            setScreen('shopZoneEdit');
          }}
          onRetry={() => void loadZones()}
          onBack={() => setScreen('shop')}
        />
      );

    case 'shopZoneEdit':
      return (
        <VendorZoneEdit
          user={user}
          zone={
            editingZone === 'new' || editingZone === null
              ? null
              : (zones?.find((z) => z.id === editingZone) ?? null)
          }
          onSaved={() => {
            void loadZones();
            void loadVendor();
            setScreen('shopZones');
          }}
          onBack={() => setScreen('shopZones')}
        />
      );

    case 'shopSchedule':
      return (
        <VendorSchedule
          user={user}
          days={schedule}
          daysOff={daysOff}
          error={scheduleError}
          onSaved={() => {
            void loadSchedule();
            void loadVendor();
          }}
          onRetry={() => void loadSchedule()}
          onBack={() => setScreen(scheduleBack)}
        />
      );

    case 'shopPayout':
      return vendor ? (
        <VendorPayout
          user={user}
          vendor={vendor}
          onVendor={setVendor}
          onBack={() => setScreen(payoutBack)}
        />
      ) : null;

    default:
      return openOrders();
  }
}

function CourierBottomNav({
  active,
  labels,
  onTab,
}: {
  active: CourierTab;
  labels: Record<CourierTab, string>;
  onTab: (tab: CourierTab) => void;
}) {
  return (
    <nav className="courier-nav" aria-label="Навигация исполнителя">
      {(['home', 'orders', 'shop', 'profile'] as const).map((tab) => (
        <button
          key={tab}
          type="button"
          aria-current={active === tab ? 'page' : undefined}
          onClick={() => onTab(tab)}
        >
          <b aria-hidden="true">
            <ThemedIcon name={TAB_ICON[tab]} />
          </b>
          {labels[tab]}
        </button>
      ))}
    </nav>
  );
}
